import React from 'react'
import styles from './Month.scss'
import _ from 'lodash'
import {Week} from '../Week'
import {DashedMetric} from 'common/DashedMetric'
import {Distance, Elevation, Duration} from 'common/units'
import {firstVisibleDay, lastVisibleDay, isTrip} from 'lib/calendarUtils'
import moment from 'moment'
import metaStyles from '../WeekMetaCell/WeekMetaCell.scss'
import PropTypes from 'prop-types'
import {pl} from 'lib/textUtils'
import cx from 'classnames'
import {Map} from 'immutable'
import {immutableSumBy} from 'lib/utils'

const getVisibleDays = (date) => {
  // These two values are likely outside the current month, but used for displaying greyed out dates on the calendar
  const firstDay = firstVisibleDay(date)
  const lastDay = lastVisibleDay(date)

  let days = []
  let current = firstDay
  while (current.isSameOrBefore(lastDay, 'day')) {
    days.push(current.clone())
    current = current.add(1, 'day')
  }

  return days
}

export class Month extends React.PureComponent {
  static propTypes = {
    date: PropTypes.instanceOf(moment).isRequired,
    accessors: PropTypes.shape({
      start: PropTypes.func.isRequired
    }),
    hideTotals: PropTypes.bool
  }

  render () {
    const {events, date, accessors, hideTotals, ...props} = this.props

    const month = getVisibleDays(date)
    const weeks = _.chunk(month, 7)

    const startOfMonth = date.clone().startOf('month')
    const monthTrips = _.times(date.daysInMonth(), i =>
      (events.get(startOfMonth.clone().add(i, 'days').format('YYYY-MM-DD'), Map())).filter(e => isTrip(e))
    ).reduce((a, b) => a.concat(b))

    const weeksTrips = weeks.map(range => (
      range.map(date => events.get(date.format('YYYY-MM-DD'), Map())).reduce((a, b) => a.concat(b)).filter(e => isTrip(e))
    ))

    return (
      <div className={styles.Month}>
        <WeekHeader range={weeks[0]} hideTotals={hideTotals} />
        {weeks.map((week, index) => (
          <Week
            key={index}
            date={date}
            events={events}
            trips={weeksTrips[index]}
            range={week}
            hideTotals={hideTotals}
            accessors={accessors}
            {...props} />
        ))}
        {!hideTotals && <MonthlyMeta monthTrips={monthTrips} weeksTrips={weeksTrips} accessors={accessors} date={date} weeks={weeks} />}
      </div>
    )
  }
}

class WeekHeader extends React.PureComponent {
  render () {
    const {range, hideTotals} = this.props

    return (<div className={styles.Header}>
      {range.map((d, i) => (<div className={styles.headerCell} key={i}>
        {moment(d).format('ddd')}
      </div>))}
      {!hideTotals && <div className={styles.headerCell} key='meta'>
        <div className={metaStyles.metaBackground}>Week Total</div>
      </div>}
    </div>)
  }
}

class MonthlyMeta extends React.PureComponent {
  render () {
    const {weeksTrips, monthTrips, weeks, date, accessors} = this.props

    const distance = immutableSumBy(monthTrips, t => t.distance)
    const elevationGain = immutableSumBy(monthTrips, t => t.elevationGain)
    const calories = immutableSumBy(monthTrips, t => t.calories)
    const movingTime = immutableSumBy(monthTrips, t => t.movingTime)

    return (
      <div style={{display: 'flex'}}>
        <div style={{flex: '1 1 0px'}}>
          <div className={styles.monthlyHeader}>
            <div className={cx(styles.title, metaStyles.metaBackground)}>Monthly Total</div>
          </div>
          <div className={styles.monthlyStats}>
            <Distance value={distance}>
              {m => <DashedMetric
                className={styles.monthlyStat}
                label={m.long}
                value={m.valueToString()} />}
            </Distance>
            <Elevation value={elevationGain}>
              {m => <DashedMetric
                className={styles.monthlyStat}
                label={`elevation gain (${m.short})`}
                value={m.valueToString()} />}
            </Elevation>
            <DashedMetric className={styles.monthlyStat}
              label={`${pl(calories, 'calorie', {includeCount: false})} burned`}
              value={calories && calories.toLocaleString('en-US')} />
            <Duration value={movingTime} bigUnits>
              {m => <DashedMetric
                className={styles.monthlyStat}
                label='hours' // we're forcing bigUnits
                value={m.valueToString()} />}
            </Duration>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <MonthlyChart weeksTrips={weeksTrips} weeks={weeks} date={date} accessors={accessors} />
        </div>
      </div>
    )
  }
}

class MonthlyChart extends React.PureComponent {
  render () {
    const {weeks, weeksTrips, date, accessors} = this.props

    let maxDistance = 0
    const milesByWeek = _.map(weeks, (week, index) => {
      const weekDate = week.find(d => d.isSame(date, 'month')) // some days in the week aren't a part of the current month
      const totalDistance = immutableSumBy(weeksTrips[index]
        .filter(t => !t.excludeFromTotals)
        .filter(t => moment(accessors.start(t)).isSame(date, 'month')), (t) => t.distance)

      if (maxDistance < totalDistance) {
        maxDistance = totalDistance
      }

      return {
        weekDate: weekDate,
        distance: totalDistance
      }
    })

    return <div className={styles.MonthlyChart}>
      {milesByWeek.map((weekData, i) => {
        const height = maxDistance ? 100 * (weekData.distance / maxDistance) : 0

        // This is a complicated way of dealing with the fact that some trips shown on the calendar aren't included in weekly totals
        const startOfWeek = moment(weekData.weekDate).startOf('week')
        const weekStart = startOfWeek.isSame(date, 'month') ? startOfWeek : moment(date).startOf('month')
        const endOfWeek = moment(weekData.weekDate).endOf('week')
        const weekEnd = endOfWeek.isSame(date, 'month') ? endOfWeek : moment(date).endOf('month')

        // data-tippy-distance places tip 10px above div.Bar on div.BarContainer hover
        // .BarContainer height is 60px, max height is 100% => scale our distance by 60%
        return <div key={i} className={styles.Week}>
          <div
            className={styles.BarContainer}
            data-tip
            title={`${weekStart.format('MM/DD')} - ${weekEnd.format('MM/DD')}`}
            data-tippy-distance={0.6 * (height - 100) + 10}>
            <div className={styles.Bar} style={{height: (height > 0 ? `${height}%` : '2px')}} />
          </div>
          <div className={styles.Label}>
            <Distance value={weekData.distance} short bigUnits />
          </div>
        </div>
      })}
    </div>
  }
}
